<template>
  <div class="flex items-center justify-end">
    <div class="px-2 py-1 text-white border rounded bg-primary">
      {{ items.length }} {{ t('general.records', items.length) }}
    </div>
    <font-awesome-icon class="w-6 h-6 p-1 mx-1 cursor-pointer hover:text-primary-500" icon="fa-light fa-refresh" @click="loadData" />
  </div>
  <div class="relative w-full overflow-y-auto flex-grow">
    <loader v-if="loading" />
    <div v-else class="flex flex-col">
      <div v-for="item in items" :key="item.id" class="flex items-center border rounded justify-between px-4 py-2 my-0.5 bg-gray-50 hover:bg-gray-100 cursor-pointer">
        <div class="grow">
          <p class="text-lg text-gray-800">
            {{ formatDateTime(item.timestamp) }}
          </p>
          <p class="text-xs text-gray-500">
            v: {{ item.version }}
          </p>
        </div>
        <tx-button
          v-if="allowCreateWhiteboard" v-tooltip="{ text: t('whiteboard.history.restoreAsSeparateBoard'), theme: { placement: 'left' } }" type="icon"
          faicon="fa-light fa-rotate-left" @click="onRestoreVersion(item)"
        />
      </div>
      <div v-if="!items.length" class="col-start-1 col-end-12">
        <p>{{ t('general.emptyList') }}</p>
      </div>
    </div>
  </div>
  <tx-dialog
    v-model="restoreVersionDialogVisible" :title="t('whiteboard.history.restoreAsSeparateBoard')" :loading="restoring"
    :cancel-state="restoring ? 'disabled' : 'enabled'" :ok-state="restoring ? 'loading' : 'enabled'"
    confirm-text="general.restore" show-ok-cancel @click="restoreVersionDialogVisible = false"
    @ok="onRestoreVersionConfirm()"
  >
    <div class="text-xl">
      <tx-alert :show="hasError" type="error" :text="errorMessage" dismissible />
      <div class="w-full my-2">
        <tx-input v-model="restoreForm.name" :label="t('whiteboard.history.enterNewBoardName')" required />
      </div>
      <div class="w-full my-2">
        <tx-switch v-model="restoreForm.shareWithSameUsers" :label="t('whiteboard.history.shareWithSameUsers')" required />
      </div>
    </div>
  </tx-dialog>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import type Whiteboard from '../../services/whiteboard'
import { useUserStore } from '@/store/userData'
import { useNotificationStore } from '@/store/notification'
import Loader from '@/shared/components/Loader.vue'
import TxButton from '@/shared/components/TxButton.vue'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxInput from '@/shared/components/TxInput.vue'
import TxSwitch from '@/shared/components/TxSwitch.vue'
import TxAlert from '@/shared/components/TxAlert.vue'
import type { SnapshotItem } from '@/api/whiteboard/model/boardModel'
import { createBoard, getBoardSnapshot, getBoardSnapshots } from '@/api/whiteboard/boards'
import { formatDateTime, isValidStringValue } from '@/services/utils'
import type { CreateUpdateWhiteboardModel, WhiteboardDetailsModel } from '@/api/t1/model/whiteboardModel'
import useErrorMessage from '@/shared/composables/errorMessage'
import { createWhiteboard } from '@/api/t1/whiteboard'
import appConfig from '@/services/appConfig'
import { privileges } from '@/models/constants'

const props = defineProps<{
  whiteboardDetails?: WhiteboardDetailsModel
  whiteboard?: Whiteboard
}>()

const { t } = useI18n()
const userStore = useUserStore()
const router = useRouter()
const notificationStore = useNotificationStore()
const { errorMessage, hasError } = useErrorMessage()

const loading = ref(false)
const items = ref<SnapshotItem[]>([])

const restoreVersionDialogVisible = ref(false)
const restoring = ref(false)
const restoredItem = ref<SnapshotItem>()
const restoreForm = reactive<{ name: string, shareWithSameUsers: boolean }>({
  name: '',
  shareWithSameUsers: false,
})

const allowCreateWhiteboard = computed(() => userStore.userProfile.isValidPrivilege(privileges.whiteboard.create))

function onRestoreVersion(item: SnapshotItem) {
  restoredItem.value = item
  restoreVersionDialogVisible.value = true
  restoreForm.name = `[restored] ${props.whiteboardDetails?.Name || ''} ${formatDateTime(item.timestamp)}`
}

function onRestoreVersionConfirm() {
  errorMessage.value = ''
  if (props.whiteboard && userStore.activeCatalog && restoredItem.value) {
    if (!isValidStringValue(restoreForm.name)) {
      errorMessage.value = t('whiteboard.history.nameIsRequired')
      return
    }
    restoring.value = true
    getBoardSnapshot(props.whiteboard.boardId, restoredItem.value.version)
      .then(async (res) => {
        if (res && res.data) {
          try {
            const payload: CreateUpdateWhiteboardModel = { Name: restoreForm.name }
            if (props.whiteboardDetails) {
              payload.FolderId = props.whiteboardDetails.FolderId
              payload.FolderName = props.whiteboardDetails.FolderName
              if (restoreForm.shareWithSameUsers) {
                payload.SharedUsers = props.whiteboardDetails.SharedUsers
                payload.SharedUsersGroups = props.whiteboardDetails.SharedUsersGroups
              }
            }
            const createWhiteboardResp = await createWhiteboard(userStore.activeCatalog!.CatalogCode, payload)
            if (createWhiteboardResp && createWhiteboardResp.data) {
              await createBoard(`${appConfig.T1Env}${createWhiteboardResp.data.Id}`, res.data.data)

              restoredItem.value = undefined
              restoreVersionDialogVisible.value = false
              notificationStore.addNotification({ message: t('whiteboard.history.restoreSuccess', { name: restoreForm.name }), type: 'Success' })
              router.push({ name: 'Whiteboards' })
            }
            else {
              errorMessage.value = t('whiteboard.history.restoreBoardFailed')
            }
          }
          catch (e: any) {
            if (e.response?.data?.error) {
              errorMessage.value = e.response.data.error
            }
            else if (e.response?.data?.length) {
              errorMessage.value = e.response.data.map(x => x.ErrorMessage).join(', ')
            }
            else {
              errorMessage.value = t('whiteboard.history.restoreBoardFailed')
            }
            console.error(e)
          }
        }
        else {
          errorMessage.value = t('whiteboard.history.restoreBoardFailed')
        }
      })
      .catch((error) => {
        if (error.response?.data?.error) {
          errorMessage.value = error.response.data.error
        }
        else {
          errorMessage.value = t('whiteboard.history.restoreBoardFailed')
        }
        console.error(error)
      })
      .finally(() => {
        restoring.value = false
      })
  }
}

function loadData() {
  items.value = []
  if (props.whiteboard && !loading.value) {
    loading.value = true
    getBoardSnapshots(props.whiteboard.boardId)
      .then((res) => {
        if (res && res.data) {
          res.data.forEach((item) => {
            items.value.push(item)
          })
        }
      })
      .catch(e => console.error(e))
      .finally(() => {
        loading.value = false
      })
  }
}

defineExpose({
  loadData,
})
</script>
